// Staff-facing Training Assessment — take your department's test (15 random
// questions, options shuffled). On submit you're told it was submitted; your
// score is NEVER shown here — HR reviews and releases it to you by email.
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { HRC, Card, Btn, Loading, input } from './hr/hrkit';

export default function Assessment() {
  const { user } = useAuth();
  const [ctx, setCtx] = useState(null);
  const [pickDept, setPickDept] = useState('');
  const [phase, setPhase] = useState('intro');   // intro | taking | done
  const [test, setTest] = useState(null);         // { token, label, questions }
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});     // { questionId: shownIndex }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/assessment/context')
      .then(r => { setCtx(r.data.data); setPickDept(r.data.data.suggested || ''); })
      .catch(e => setErr(e.response?.data?.error || 'Could not load assessment'));
  }, []);

  const start = () => {
    setErr(''); setBusy(true);
    const params = ctx?.locked ? {} : { department: pickDept };
    api.get('/assessment/my-test', { params })
      .then(r => { setTest(r.data.data); setAnswers({}); setIdx(0); setPhase('taking'); })
      .catch(e => setErr(e.response?.data?.error || 'Could not start the test'))
      .finally(() => setBusy(false));
  };

  const submit = () => {
    setErr(''); setBusy(true);
    api.post('/assessment/submit', { token: test.token, answers })
      .then(() => setPhase('done'))
      .catch(e => setErr(e.response?.data?.error || 'Submit failed'))
      .finally(() => setBusy(false));
  };

  const reset = () => { setPhase('intro'); setTest(null); setAnswers({}); setIdx(0); setErr(''); };

  const wrap = (children) => (
    <div style={{ padding: 24, background: HRC.pageBg, minHeight: '100%' }}>
      <h2 style={{ margin: '0 0 4px', color: HRC.sidebar, fontSize: 22 }}>Training Assessment</h2>
      <div style={{ width: 60, height: 3, background: HRC.red, borderRadius: 2, marginBottom: 18 }} />
      <div style={{ maxWidth: 760 }}>{children}</div>
    </div>
  );

  if (!ctx && !err) return wrap(<Loading />);

  // ── INTRO ──
  if (phase === 'intro') {
    return wrap(
      <Card>
        {err && <Banner err>{err}</Banner>}
        <p style={{ marginTop: 0, color: HRC.text, fontSize: 14 }}>
          Hello {user?.name?.split(' ')[0] || 'there'} — this is a {ctx?.draw || 15}-question test
          {ctx?.locked ? <> for <b>{ctx.suggested_label}</b></> : null}. The pass mark is <b>{ctx?.pass_pct || 90}%</b>.
        </p>
        <ul style={{ color: HRC.muted, fontSize: 13, lineHeight: 1.7, paddingLeft: 18 }}>
          <li>Questions and answer options are randomised each attempt.</li>
          <li>Your score is <b>not</b> shown here — HR reviews it and emails you the result.</li>
          <li>You can retake the assessment as many times as you like.</li>
        </ul>

        {!ctx?.locked && (
          <div style={{ margin: '14px 0' }}>
            <span style={{ fontSize: 11, color: '#888', fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>Choose your test</span>
            <select value={pickDept} onChange={e => setPickDept(e.target.value)} style={{ ...input, width: 'auto', minWidth: 260 }}>
              <option value="">— select a department —</option>
              {(ctx?.departments || []).map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
        )}

        <Btn onClick={start} disabled={busy || (!ctx?.locked && !pickDept)} style={{ marginTop: 6 }}>
          {busy ? 'Loading…' : 'Start Test'}
        </Btn>
      </Card>
    );
  }

  // ── DONE ──
  if (phase === 'done') {
    return wrap(
      <Card style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
        <h3 style={{ margin: '0 0 8px', color: HRC.sidebar }}>Submitted</h3>
        <p style={{ color: HRC.muted, fontSize: 14, maxWidth: 460, margin: '0 auto 18px' }}>
          Your assessment has been submitted and is <b>awaiting HR review</b>. You'll receive
          your result by email once HR has verified it.
        </p>
        <Btn variant="soft" onClick={reset}>Back</Btn>
      </Card>
    );
  }

  // ── TAKING ──
  const q = test.questions[idx];
  const chosen = answers[q.id];
  const answeredCount = Object.keys(answers).length;
  const last = idx === test.questions.length - 1;
  return wrap(
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: HRC.muted, fontWeight: 700 }}>{test.label} · Question {idx + 1} of {test.questions.length}</div>
        <div style={{ fontSize: 12, color: HRC.muted }}>{answeredCount}/{test.questions.length} answered</div>
      </div>
      {/* progress bar */}
      <div style={{ height: 4, background: HRC.border, borderRadius: 2, marginBottom: 18 }}>
        <div style={{ height: '100%', width: `${((idx + 1) / test.questions.length) * 100}%`, background: HRC.navy, borderRadius: 2 }} />
      </div>

      <div style={{ fontSize: 15, fontWeight: 600, color: HRC.text, marginBottom: 14 }}>{q.question}</div>
      <div style={{ display: 'grid', gap: 8 }}>
        {q.options.map((opt, i) => {
          const on = chosen === i;
          return (
            <button key={i} onClick={() => setAnswers({ ...answers, [q.id]: i })}
              style={{ textAlign: 'left', padding: '11px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 14,
                border: `1.5px solid ${on ? HRC.navy : HRC.border}`, background: on ? HRC.violet : '#fff', color: HRC.text,
                display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${on ? HRC.navy : '#bbb'}`,
                background: on ? HRC.navy : '#fff', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 11 }}>{on ? '✓' : ''}</span>
              {opt}
            </button>
          );
        })}
      </div>

      {err && <Banner err>{err}</Banner>}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
        <Btn variant="ghost" onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0}>← Previous</Btn>
        {last
          ? <Btn onClick={submit} disabled={busy}>{busy ? 'Submitting…' : `Submit Test${answeredCount < test.questions.length ? ` (${test.questions.length - answeredCount} blank)` : ''}`}</Btn>
          : <Btn onClick={() => setIdx(i => Math.min(test.questions.length - 1, i + 1))}>Next →</Btn>}
      </div>
    </Card>
  );
}

const Banner = ({ children }) => (
  <div style={{ background: '#FCEBEB', color: '#B91C1C', padding: '8px 12px', borderRadius: 8, fontSize: 13, margin: '12px 0' }}>{children}</div>
);
